import React, { useEffect, useRef, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import axios from 'axios'
import {
  BsArrowRight,
  BsQrCodeScan,
  BsSearch,
  BsShieldCheck,
  BsLightningCharge,
  BsGraphUp,
  BsCashStack,
} from 'react-icons/bs'
import Header from './layouts/Header'
import { API_URL } from './config'
import './App.css'
import './Theme.css'
import './FeesPage.css'
import './Home.css'
import './Landing.css'

const Ticket = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinejoin="round"
    strokeLinecap="round"
    {...props}
  >
    <path d="M4 6h16a1 1 0 0 1 1 1v3a2 2 0 0 0 0 4v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a2 2 0 0 0 0-4V7a1 1 0 0 1 1-1z" />
    <path d="M14 8v1.5M14 11.25v1.5M14 14.5V16" />
  </svg>
)

const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setSeen(true)
      return undefined
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true)
          io.disconnect()
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return (
    <div
      ref={ref}
      className={`fp-reveal ${seen ? 'is-in' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

const JOURNEY = [
  { icon: <BsSearch />, title: 'Discover', text: 'Find events by name, venue or category.' },
  { icon: <Ticket />, title: 'Buy', text: 'Pay online and get your digital ticket.' },
  { icon: <BsQrCodeScan />, title: 'Scan', text: 'Show it at the door. One scan, you are in.' },
]

const FACTS = ['No queues', 'Paper-free', 'One scan per ticket', 'Pay online', 'Clear payouts']

const PILLARS = [
  {
    icon: <Ticket />,
    title: 'Digital ticketing',
    text: 'Buy online, get your ticket instantly. No queues, no paper, no lost stubs.',
  },
  {
    icon: <BsSearch />,
    title: 'Explore events',
    text: 'Search by name, venue, category or province and find what is on near you.',
  },
  {
    icon: <BsQrCodeScan />,
    title: 'Fast scanning',
    text: 'Organisers scan tickets at the door with the Karamale scanner app. Every ticket is verified once.',
  },
]

const STEPS = [
  ['1', 'Find an event', 'Browse concerts, festivals and experiences.'],
  ['2', 'Buy your ticket', 'Pick a ticket type and pay online.'],
  ['3', 'Walk in', 'Show your ticket at the door. It gets scanned. You are in.'],
]

const PERKS = [
  [<BsLightningCharge key="a" />, 'Instant entry', 'Scans take a moment, so queues keep moving.'],
  [
    <BsShieldCheck key="b" />,
    'Fraud-resistant',
    'Each ticket is unique and can only be used once.',
  ],
  [
    <Ticket key="c" />,
    'Transparent payouts',
    'Organisers see sales as they happen and get paid clearly.',
  ],
]

const WS = `${API_URL}/apps/kscanner/webservices.php`

// Sample organiser data for the dashboard preview (illustrative only).
const SAMPLE_REVENUE = [12, 18, 15, 26, 31, 29, 42, 48, 45, 62]
const SAMPLE_SALES = [34, 52, 41, 68, 90, 74, 120]
const SAMPLE_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const areaPaths = (data, w, h, pad = 8) => {
  const max = Math.max(...data)
  const step = (w - pad * 2) / (data.length - 1)
  const pts = data.map((v, i) => [pad + i * step, h - pad - (v / max) * (h - pad * 2)])
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const area = `${line} L${pts[pts.length - 1][0].toFixed(1)},${h} L${pts[0][0].toFixed(1)},${h} Z`
  return { line, area, last: pts[pts.length - 1] }
}

const Landing = () => {
  const [live, setLive] = useState({ events: null, categories: null, provinces: null })

  useEffect(() => {
    document.title = "Karamale - World's digital ticket software"
    const count = (res) => (Array.isArray(res.data) ? res.data.length : null)
    const get = (params) =>
      axios
        .get(WS, { params })
        .then(count)
        .catch(() => null)
    let alive = true
    Promise.all([
      get({ query: 'EVENTS' }),
      get({ query: 'EVENTCATEGORY' }),
      get({ query: 'PROVINCES' }),
    ]).then(([events, categories, provinces]) => {
      if (alive) setLive({ events, categories, provinces })
    })
    return () => {
      alive = false
    }
  }, [])

  const liveStats = [
    [live.events, 'Events on sale'],
    [live.categories, 'Event categories'],
    [live.provinces, 'Provinces covered'],
  ].filter(([n]) => n)
  const rev = areaPaths(SAMPLE_REVENUE, 360, 150)
  const salesMax = Math.max(...SAMPLE_SALES)

  // Signed-in users skip the marketing page.
  if (localStorage.getItem('customername') !== null) return <Redirect to="/events" />
  if (
    localStorage.getItem('businessname') !== null &&
    localStorage.getItem('accounttype') !== null
  ) {
    return <Redirect to="/dashboard" />
  }
  if (localStorage.getItem('partnerid') !== null) return <Redirect to="/in" />

  return (
    <div className="fp-page lp-page">
      <Header />

      {/* Hero */}
      <section className="fp-hero" aria-labelledby="lp-title">
        <div className="fp-aurora" aria-hidden="true">
          <span className="fp-aurora__a" />
          <span className="fp-aurora__b" />
        </div>
        <div className="fp-wrap lp-hero mt-4">
          <div>
            <span className="fp-pill">
              <i />
              Ticketing · Discovery · Scanning
            </span>
            <h1 id="lp-title">
              Every event. <br />
              One <span className="lp-grad">digital ticket.</span>
            </h1>
            <p className="fp-hero__sub">
              Karamale takes you from finding an event to walking through the door. Buy tickets
              online, and organisers scan them in seconds.
            </p>
            <div className="fp-actions">
              <Link to="/events" className="fp-btn fp-btn--brand">
                Explore events <BsArrowRight aria-hidden="true" />
              </Link>
              <Link to="/organiser-registration" className="fp-btn fp-btn--ghost">
                Sell tickets
              </Link>
            </div>
          </div>

          <div className="lp-hero__visual" aria-hidden="true">
            <div className="lp-ticket">
              <small>Admit one</small>
              <Ticket />
              <div className="lp-ticket__line" />
              <div className="lp-ticket__qr">
                <BsQrCodeScan />
                <span>Scan at the door</span>
              </div>
            </div>
            <span className="lp-chip lp-chip--a">
              <BsLightningCharge /> Instant delivery
            </span>
            <span className="lp-chip lp-chip--b">
              <BsShieldCheck /> QR verified
            </span>
            <span className="lp-chip lp-chip--c">
              <BsSearch /> Pay online
            </span>
          </div>
        </div>
      </section>

      {/* Journey infographic banner */}
      <section className="lp-journey" aria-label="From discovery to the door">
        <div className="fp-wrap">
          <Reveal>
            <div className="lp-journey__card">
              <div className="lp-journey__glow" aria-hidden="true" />
              <p className="lp-journey__eyebrow">The Karamale journey</p>
              <div className="lp-journey__flow">
                {JOURNEY.map((s, i) => (
                  <React.Fragment key={s.title}>
                    <div className="lp-node">
                      <span className="lp-node__icon">{s.icon}</span>
                      <strong>{s.title}</strong>
                      <p>{s.text}</p>
                    </div>
                    {i < JOURNEY.length - 1 && (
                      <div className="lp-link" aria-hidden="true">
                        <i />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="lp-facts">
                {FACTS.map((f) => (
                  <span key={f}>{f}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="fp-section" aria-labelledby="lp-what">
        <div className="fp-wrap">
          <Reveal>
            <h2 id="lp-what" className="fp-h2">
              Everything an event <span>needs.</span>
            </h2>
            <p className="fp-lead">One platform for the people going and the people hosting.</p>
          </Reveal>
          <div className="lp-grid">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 90}>
                <div className="lp-card">
                  <span className="lp-card__icon">{p.icon}</span>
                  <h3>{p.title}</h3>
                  <p>{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="fp-section" aria-labelledby="lp-how">
        <div className="fp-wrap">
          <div className="fp-flow">
            <div>
              <h2 id="lp-how" className="fp-h2">
                How it <span>works.</span>
              </h2>
              <p className="fp-lead">Three steps from search to the door.</p>
              <Link to="/events" className="fp-btn fp-btn--brand lp-mt">
                Explore events <BsArrowRight aria-hidden="true" />
              </Link>
            </div>
            <ol className="fp-steps">
              {STEPS.map(([n, title, text]) => (
                <li key={n}>
                  <span className="fp-steps__n">{n}</span>
                  <div>
                    <strong>{title}</strong>
                    <p>{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Scanning */}
      <section className="fp-section" aria-labelledby="lp-scan">
        <div className="fp-wrap lp-split">
          <Reveal>
            <div className="lp-phone" aria-hidden="true">
              <div className="lp-phone__screen">
                <div className="lp-phone__frame">
                  <i /> <i /> <i /> <i />
                  <BsQrCodeScan />
                  <b />
                </div>
                <div className="lp-phone__ok">Valid ticket</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2 id="lp-scan" className="fp-h2">
              Scan. Verify. <span>Next.</span>
            </h2>
            <p className="fp-lead">
              The Karamale scanner app checks every ticket at the door, so entry is fast and each
              ticket works once.
            </p>
            <ul className="lp-perks">
              {PERKS.map(([icon, title, text]) => (
                <li key={title}>
                  <span className="lp-card__icon lp-card__icon--sm">{icon}</span>
                  <div>
                    <strong>{title}</strong>
                    <p>{text}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="fp-actions lp-mt">
              <Link to="/kscanner" className="fp-btn fp-btn--brand">
                Learn more about scanning <BsArrowRight aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Live platform numbers (real, from the API; hidden if unavailable) */}
      {liveStats.length > 0 && (
        <section className="lp-live" aria-label="Karamale right now">
          <div className="fp-wrap">
            <Reveal>
              <div className="lp-live__row">
                {liveStats.map(([n, label]) => (
                  <div key={label} className="lp-live__item">
                    <strong>{n}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Organiser analytics preview */}
      <section className="fp-section" aria-labelledby="lp-dash">
        <div className="fp-wrap">
          <Reveal>
            <h2 id="lp-dash" className="fp-h2">
              Know your sales, <span>live.</span>
            </h2>
            <p className="fp-lead">
              Organisers get real-time revenue, ticket sales and scan stats in one dashboard.
            </p>
          </Reveal>

          <Reveal delay={100}>
            <div className="lp-dash" aria-label="Sample organiser dashboard">
              <div className="lp-dash__bar">
                <span className="lp-dash__dots">
                  <i /> <i /> <i />
                </span>
                <span className="lp-dash__tag">Sample data</span>
              </div>

              <div className="lp-kpis">
                <div className="lp-kpi">
                  <span className="lp-kpi__icon">
                    <BsCashStack />
                  </span>
                  <small>Revenue</small>
                  <strong>R 48 250</strong>
                  <em>+18% vs last week</em>
                </div>
                <div className="lp-kpi">
                  <span className="lp-kpi__icon">
                    <Ticket />
                  </span>
                  <small>Tickets sold</small>
                  <strong>479</strong>
                  <em>+12% vs last week</em>
                </div>
                <div className="lp-kpi">
                  <span className="lp-kpi__icon">
                    <BsQrCodeScan />
                  </span>
                  <small>Scanned in</small>
                  <strong>92%</strong>
                  <em>of tickets sold</em>
                </div>
              </div>

              <div className="lp-charts">
                <div className="lp-chart">
                  <p className="lp-chart__title">
                    <BsGraphUp /> Revenue over time
                  </p>
                  <svg viewBox="0 0 360 150" role="img" aria-label="Sample revenue trend rising">
                    <defs>
                      <linearGradient id="lp-area" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#d91f2d" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#d91f2d" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {[0.25, 0.5, 0.75].map((g) => (
                      <line
                        key={g}
                        x1="0"
                        x2="360"
                        y1={150 * g}
                        y2={150 * g}
                        className="lp-grid-line"
                      />
                    ))}
                    <path d={rev.area} fill="url(#lp-area)" />
                    <path d={rev.line} className="lp-line" />
                    <circle cx={rev.last[0]} cy={rev.last[1]} r="5" className="lp-dot" />
                  </svg>
                </div>

                <div className="lp-chart">
                  <p className="lp-chart__title">
                    <Ticket /> Tickets sold per day
                  </p>
                  <div className="lp-bars" role="img" aria-label="Sample daily ticket sales">
                    {SAMPLE_SALES.map((v, i) => (
                      <div className="lp-bar" key={SAMPLE_DAYS[i]}>
                        <span className="lp-bar__v">{v}</span>
                        <i style={{ height: `${(v / salesMax) * 100}%` }} />
                        <span className="lp-bar__d">{SAMPLE_DAYS[i]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Organiser + affiliate */}
      <section className="fp-section">
        <div className="fp-wrap fp-two fp-cta-grid">
          <Reveal className="fp-cell">
            <div className="fp-cta fp-cta--left">
              <h2>
                Sell tickets on <em>Karamale.</em>
              </h2>
              <p>
                Create an organiser account, publish your event and get paid with transparent
                payouts.
              </p>
              <div className="fp-actions">
                <Link to="/organiser-registration" className="fp-btn fp-btn--light">
                  Get started
                </Link>
                <Link to="/about" className="fp-btn fp-btn--outline">
                  Learn more
                </Link>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120} className="fp-cell">
            <div className="fp-card fp-card--hot fp-ctacard">
              <h2 className="fp-h2">
                Earn as an <span>affiliate.</span>
              </h2>
              <p>
                Refer organisers with your unique link. When they start selling tickets, you earn up
                to 30%.
              </p>
              <div className="fp-actions">
                <Link to="/affiliate-registration" className="fp-btn fp-btn--brand">
                  Get started
                </Link>
                <Link to="/aboutsalesparnter" className="fp-btn fp-btn--ghost">
                  Learn more
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="fp-section fp-section--last">
        <div className="fp-wrap">
          <Reveal>
            <div className="lp-final">
              <h2>Ready for your next event?</h2>
              <p>See what is on and grab your ticket.</p>
              <Link to="/events" className="fp-btn fp-btn--light">
                Explore events <BsArrowRight aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  )
}

export default Landing
